/**
 * Ask Velora as a floating assistant on every screen (it replaced the
 * "AI Assistant" side-nav page): a launcher with the Velora tooth mark in
 * the bottom-right corner, and a frosted charcoal chat panel over the page.
 *
 * - Ctrl+J (Cmd+J) opens and closes it; Escape closes it and returns focus
 *   to the launcher.
 * - Drag the launcher or the panel's title bar to move it; the place and the
 *   open state are kept for the browser session (store.ts).
 * - Drag the panel's edges or corners to resize it (the top-left corner and
 *   the left edge are the natural grips, as it sits bottom-right); the size
 *   is kept in localStorage. The maximize button (or a double-click on the
 *   title bar) fills the window; Escape leaves full screen first.
 * - On a phone-width window it never sits over the page: it collapses to a
 *   small tab on the right edge, and opens full screen only when asked.
 * - Same API as the /assistant workspace (AskPanel, assistantApi); the
 *   workspace page still holds scribe, automations and saved conversations.
 *
 * The chat itself loads on first open (WidgetPanel is lazy), so the shell
 * does not pay for it on boot.
 */
import { Link } from '@tanstack/react-router';
import { BrandGlyph, Kbd, Tooltip } from '@velora/ui';
import { ExternalLink, Maximize2, Minimize2, Minus } from 'lucide-react';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useEntitlement, usePermission, useShortcut } from '../../../app/shell.js';
import {
  assistantWidget,
  clampPosition,
  fitSize,
  MIN_SIZE,
  useAssistantWidget,
  type WidgetPosition,
  type WidgetSize,
} from './store.js';
import './widget.css';

const WidgetPanel = lazy(() => import('./WidgetPanel.js'));

export const WIDGET_SHORTCUT = 'mod+j';
// Under 900 px (phones and an unfolded foldable, mobile audit 2026-10-21): the
// launcher is a small edge tab that slides away while the page scrolls.
const NARROW_QUERY = '(width < 900px)';
const LAUNCHER = { width: 52, height: 52 };
/** The phone edge tab (widget.css `.is-narrow .vaw-launcher`) and its resting place. */
const NARROW_TAB = { width: 20, height: 40 };
const NARROW_PLACE: WidgetPosition = { right: 0, bottom: 96 };
/** How long the edge tab stays away after the last scroll. */
const SCROLL_HIDE_MS = 1200;

function useNarrow(): boolean {
  const get = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(NARROW_QUERY).matches
      : false;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(NARROW_QUERY);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

function viewport() {
  return { width: window.innerWidth, height: window.innerHeight };
}

function useViewport() {
  const [vp, setVp] = useState(viewport);
  useEffect(() => {
    const on = () => setVp(viewport());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return vp;
}

/**
 * Pointer drag that moves the widget's bottom-right anchor. Returns whether
 * the last press moved (so the launcher can tell a drag from a click).
 */
function useDrag(size: { width: number; height: number }) {
  const moved = useRef(false);
  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      // Buttons and links inside the title bar keep their own clicks.
      if (target.closest('[data-no-drag]')) return;
      const start = { x: e.clientX, y: e.clientY, pos: assistantWidget.get().pos };
      moved.current = false;
      const el = e.currentTarget;
      el.setPointerCapture?.(e.pointerId);
      const move = (ev: PointerEvent) => {
        const dx = ev.clientX - start.x;
        const dy = ev.clientY - start.y;
        if (!moved.current && Math.hypot(dx, dy) < 4) return;
        moved.current = true;
        assistantWidget.move(
          clampPosition(
            { right: start.pos.right - dx, bottom: start.pos.bottom - dy },
            size,
            viewport(),
          ),
        );
      };
      const up = () => {
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerup', up);
        el.removeEventListener('pointercancel', up);
      };
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
    },
    [size],
  );
  return { onPointerDown, moved };
}

/** Which sides a resize grip moves: n(orth), s(outh), e(ast), w(est). */
export type Edge = 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';
const EDGES: Edge[] = ['n', 's', 'e', 'w', 'nw', 'ne', 'sw', 'se'];
const EDGE_MARGIN = 8;

/**
 * The panel's new size and anchor when the `edge` grip moves by (dx, dy)
 * from the `start` box: the opposite sides stay put, every side stays inside
 * the window margin, and the size stays between MIN_SIZE and what fits.
 */
export function resizeBox(
  start: { size: WidgetSize; pos: WidgetPosition },
  edge: Edge,
  dx: number,
  dy: number,
  vp: { width: number; height: number },
): { size: WidgetSize; pos: WidgetPosition } {
  const m = EDGE_MARGIN;
  let right = vp.width - start.pos.right;
  let bottom = vp.height - start.pos.bottom;
  let left = right - start.size.width;
  let top = bottom - start.size.height;
  const clampX = (x: number) => Math.min(vp.width - m, Math.max(m, x));
  const clampY = (y: number) => Math.min(vp.height - m, Math.max(m, y));
  if (edge.includes('w')) left = clampX(left + dx);
  if (edge.includes('e')) right = clampX(right + dx);
  if (edge.includes('n')) top = clampY(top + dy);
  if (edge.includes('s')) bottom = clampY(bottom + dy);
  const size = fitSize({ width: right - left, height: bottom - top }, vp, m);
  // A size held at the minimum grows away from the side that stays put.
  if (edge.includes('w')) left = right - size.width;
  else right = left + size.width;
  if (edge.includes('n')) top = bottom - size.height;
  else bottom = top + size.height;
  right = Math.min(right, vp.width - m);
  bottom = Math.min(bottom, vp.height - m);
  return {
    size,
    pos: { right: Math.round(vp.width - right), bottom: Math.round(vp.height - bottom) },
  };
}

/** Pointer drags on the panel's edge grips, and arrow keys on the focusable corner grip. */
function useResize(box: { size: WidgetSize; pos: WidgetPosition }) {
  const [resizing, setResizing] = useState(false);
  const boxRef = useRef(box);
  boxRef.current = box;

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const edge = e.currentTarget.dataset['edge'] as Edge | undefined;
    if (!edge) return;
    e.preventDefault();
    e.stopPropagation();
    const start = { x: e.clientX, y: e.clientY, box: boxRef.current };
    const el = e.currentTarget;
    el.setPointerCapture?.(e.pointerId);
    setResizing(true);
    let last = start.box;
    const move = (ev: PointerEvent) => {
      last = resizeBox(start.box, edge, ev.clientX - start.x, ev.clientY - start.y, viewport());
      assistantWidget.resize(last.size, last.pos, false);
    };
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      assistantWidget.resize(last.size, last.pos, true);
      setResizing(false);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  }, []);

  const onKeyDown = useCallback((e: ReactKeyboardEvent<HTMLElement>) => {
    const step = e.shiftKey ? 80 : 20;
    const deltas: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const delta = deltas[e.key];
    if (!delta) return;
    e.preventDefault();
    const next = resizeBox(boxRef.current, 'nw', delta[0], delta[1], viewport());
    assistantWidget.resize(next.size, next.pos, true);
  }, []);

  return { resizing, onPointerDown, onKeyDown };
}

const INTERACTIVE =
  'button, a[href], input, select, textarea, [role="button"], [role="tab"], [role="link"], [role="toolbar"]';
/** Side panels and drawers: a launcher that would have to float over one hides instead. */
const PANEL = 'aside, [role="complementary"], .vel-drawer';
/** Probe spacing: smaller than any control (a 24 px button cannot slip between probes). */
const PROBE_STEP = 10;

/**
 * Points covering the launcher's box plus a 4 px margin, at most PROBE_STEP
 * apart. The old five-point probe (corners and centre) missed a 24 px "Book"
 * button sitting between them (r18 role test SC-01 / HY-08 / TC-06).
 */
export function probePoints(
  left: number,
  top: number,
  right: number,
  bottom: number,
): Array<[number, number]> {
  const xs = steps(left - 4, right + 4);
  const ys = steps(top - 4, bottom + 4);
  return ys.flatMap((y) => xs.map((x): [number, number] => [x, y]));
}

function steps(a: number, b: number): number[] {
  const n = Math.max(1, Math.ceil((b - a) / PROBE_STEP));
  return Array.from({ length: n + 1 }, (_, i) => a + ((b - a) * i) / n);
}

function box(place: WidgetPosition, size: { width: number; height: number }, lift: number) {
  const right = window.innerWidth - place.right;
  const bottom = window.innerHeight - place.bottom - lift;
  return { left: right - size.width, top: bottom - size.height, right, bottom };
}

/** The first element under (x, y) that is not part of the widget. */
function under(
  root: Element | null | undefined,
  hit: (x: number, y: number) => Element[],
  x: number,
  y: number,
): Element | undefined {
  return hit(x, y).find((el) => !root?.contains(el));
}

/**
 * How far to lift the closed launcher so it does not sit on a control of
 * the page under it (the schedule's bottom toolbar, a sticky Save bar):
 * points across the launcher's box (and a 4 px margin) are probed at its
 * place, then 8 px higher at a time, up to 320 px. A toolbar counts as a
 * control. 0 when the spot is already clear.
 */
export function clearLift(
  launcher: HTMLElement | null,
  place: WidgetPosition,
  size = LAUNCHER,
  hit: (x: number, y: number) => Element[] = (x, y) => document.elementsFromPoint(x, y),
): number {
  if (!launcher || typeof window === 'undefined') return 0;
  const root = launcher.closest('.vaw-root');
  const blocked = (lift: number) => {
    const b = box(place, size, lift);
    return probePoints(b.left, b.top, b.right, b.bottom).some(([x, y]) =>
      Boolean(under(root, hit, x, y)?.closest(INTERACTIVE)),
    );
  };
  for (let lift = 0; lift <= 320; lift += 8) if (!blocked(lift)) return lift;
  return 0;
}

/**
 * Where the closed launcher goes: lifted clear of page controls, or hidden
 * while a modal dialog is open, while a control under it sits in a side
 * panel or drawer (its Save / Book footer: lifting would only float the
 * launcher over the panel's fields), or when no clear spot exists within
 * 320 px. App-wide: the launcher must never cover a Save or Book button
 * (scheduler role test 2026-10-20, r18 SC-01 / HY-08 / TC-06). Ctrl+J still
 * opens Ask Velora while it is hidden.
 */
export function placeLauncher(
  launcher: HTMLElement | null,
  place: WidgetPosition,
  size = LAUNCHER,
  hit: (x: number, y: number) => Element[] = (x, y) => document.elementsFromPoint(x, y),
  doc: Pick<Document, 'querySelectorAll'> | null = typeof document === 'undefined'
    ? null
    : document,
): { lift: number; hidden: boolean } {
  if (!launcher || typeof window === 'undefined') return { lift: 0, hidden: false };
  const root = launcher.closest('.vaw-root');
  const modal = [...(doc?.querySelectorAll('[role="dialog"], [role="alertdialog"]') ?? [])].some(
    (d) => !root?.contains(d) && d.getAttribute('aria-hidden') !== 'true',
  );
  if (modal) return { lift: 0, hidden: true };
  const b = box(place, size, 0);
  const controls = probePoints(b.left, b.top, b.right, b.bottom)
    .map(([x, y]) => under(root, hit, x, y)?.closest(INTERACTIVE))
    .filter((el): el is Element => Boolean(el));
  if (controls.length === 0) return { lift: 0, hidden: false };
  // A panel's footer button under the launcher: hide rather than float over the panel.
  if (controls.some((el) => el.closest(PANEL))) return { lift: 0, hidden: true };
  const lift = clearLift(launcher, place, size, hit);
  return lift > 0 ? { lift, hidden: false } : { lift: 0, hidden: true };
}

export function AssistantWidget({
  hidden = false,
  routeKey,
}: {
  hidden?: boolean;
  /** The current path: the launcher re-checks what it sits on after each page change. */
  routeKey?: string;
}) {
  const { open, pos, size, full, pending } = useAssistantWidget();
  const canAsk = usePermission('patient.view');
  const entitlement = useEntitlement('ai_assistant');
  const available = canAsk && entitlement.readable && !hidden;
  const narrow = useNarrow();
  const vp = useViewport();
  // Phone width is always the full-screen sheet; the toggle is for wider windows.
  const isFull = full && !narrow;
  const launcherRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [everOpened, setEverOpened] = useState(open);

  const close = useCallback(() => {
    assistantWidget.setOpen(false);
    requestAnimationFrame(() => launcherRef.current?.focus());
  }, []);
  const toggle = useCallback(() => {
    if (assistantWidget.get().open) close();
    else assistantWidget.setOpen(true);
  }, [close]);
  const toggleFull = useCallback(() => assistantWidget.setFull(!assistantWidget.get().full), []);

  // An answer's link (a patient, chart, ledger) changed the page: step out of
  // full screen (and close the phone sheet) so the page it opened is seen.
  // The floating panel keeps the conversation, which is the way back
  // (Zac 2026-10-28: "no way back" from Ask Velora).
  const lastRoute = useRef(routeKey);
  useEffect(() => {
    if (lastRoute.current === routeKey) return;
    lastRoute.current = routeKey;
    const st = assistantWidget.get();
    if (!st.open) return;
    if (narrow) assistantWidget.setOpen(false);
    else if (st.full) assistantWidget.setFull(false);
  }, [routeKey, narrow]);

  useShortcut(WIDGET_SHORTCUT, 'Open or close Ask Velora', toggle, {
    section: 'Global',
    allowInInputs: true,
    enabled: available,
    id: 'assistant.widget',
  });

  // Phone width: collapse to the launcher (a session's saved "open" never
  // covers the page there; opening again is the user's choice).
  useEffect(() => {
    if (narrow && assistantWidget.get().open) assistantWidget.setOpen(false);
  }, [narrow]);

  useEffect(() => {
    if (!open) return;
    setEverOpened(true);
    const t = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, [open]);

  // Escape leaves full screen even when focus has slipped out of the panel
  // (the panel's own handler covers focus inside it).
  useEffect(() => {
    if (!open || !isFull) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('.vaw, [role="menu"], [role="dialog"], [role="listbox"]')) return;
      e.preventDefault();
      assistantWidget.setFull(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isFull]);

  // A saved size and place from a bigger window must still fit and be on screen.
  const panelSize = useMemo(() => fitSize(size, vp), [size, vp]);
  const place = useMemo<WidgetPosition>(
    () => clampPosition(pos, open ? panelSize : LAUNCHER, vp),
    [pos, open, panelSize, vp],
  );

  // Never cover a page control with the closed launcher.
  const [lift, setLift] = useState(0);
  const [tucked, setTucked] = useState(false);
  useEffect(() => {
    if (open || !available) {
      setLift(0);
      setTucked(false);
      return undefined;
    }
    const check = () => {
      const spot = narrow
        ? placeLauncher(launcherRef.current, NARROW_PLACE, NARROW_TAB)
        : placeLauncher(launcherRef.current, place);
      setLift(spot.lift);
      setTucked(spot.hidden);
    };
    const timers = [250, 1200, 3000].map((ms) => window.setTimeout(check, ms));
    window.addEventListener('resize', check);
    // Side panels and dialogs open without a route change: re-check when the
    // page's structure changes (debounced), so a new Save bar is never covered.
    let pendingCheck = 0;
    const mo =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(() => {
            window.clearTimeout(pendingCheck);
            pendingCheck = window.setTimeout(check, 150);
          });
    mo?.observe(document.body, { childList: true, subtree: true });
    // A panel that slides in has its final place only when its animation ends.
    const settle = () => {
      window.clearTimeout(pendingCheck);
      pendingCheck = window.setTimeout(check, 50);
    };
    document.addEventListener('transitionend', settle, true);
    document.addEventListener('animationend', settle, true);
    return () => {
      for (const t of timers) window.clearTimeout(t);
      window.clearTimeout(pendingCheck);
      document.removeEventListener('transitionend', settle, true);
      document.removeEventListener('animationend', settle, true);
      mo?.disconnect();
      window.removeEventListener('resize', check);
    };
  }, [routeKey, place, open, narrow, available]);

  // Narrow: the edge tab slides out while any part of the page scrolls, so
  // it never sits on the content being read (mobile audit 2026-10-21).
  const [scrolling, setScrolling] = useState(false);
  useEffect(() => {
    if (!narrow || open || !available) {
      setScrolling(false);
      return undefined;
    }
    let t = 0;
    const onScroll = (e: Event) => {
      const target = e.target;
      if (target instanceof Element && target.closest('.vaw-root')) return;
      setScrolling(true);
      window.clearTimeout(t);
      t = window.setTimeout(() => setScrolling(false), SCROLL_HIDE_MS);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [narrow, open, available]);

  const launcherDrag = useDrag(LAUNCHER);
  const panelDrag = useDrag(panelSize);
  const resize = useResize({ size: panelSize, pos: place });

  if (!available) return null;

  const canMove = !narrow && !isFull;
  const style = canMove
    ? {
        right: place.right,
        bottom: place.bottom,
        width: panelSize.width,
        height: panelSize.height,
      }
    : undefined;
  const launcherStyle = narrow
    ? lift > 0
      ? { bottom: NARROW_PLACE.bottom + lift }
      : undefined
    : { right: place.right, bottom: place.bottom + lift };

  return (
    <div className={`vaw-root ${narrow ? 'is-narrow' : ''}`} data-open={open ? '' : undefined}>
      <Tooltip side="left" content="Ask Velora" shortcut={['Ctrl', 'J']}>
        <button
          ref={launcherRef}
          type="button"
          className="vaw-launcher"
          data-theme="dark"
          style={open || tucked ? { ...launcherStyle, visibility: 'hidden' } : launcherStyle}
          data-tucked={tucked ? '' : undefined}
          data-scrolling={scrolling ? '' : undefined}
          aria-label="Ask Velora"
          aria-expanded={open}
          aria-controls="vaw-panel"
          aria-keyshortcuts="Control+J Meta+J"
          onPointerDown={narrow ? undefined : launcherDrag.onPointerDown}
          onClick={() => {
            if (launcherDrag.moved.current) {
              launcherDrag.moved.current = false;
              return;
            }
            toggle();
          }}
        >
          <BrandGlyph size={narrow ? 16 : 28} />
        </button>
      </Tooltip>

      {everOpened || open ? (
        <section
          id="vaw-panel"
          className={`vaw${isFull ? ' is-full' : ''}`}
          data-theme="dark"
          data-resizing={resize.resizing ? '' : undefined}
          role="dialog"
          aria-modal={narrow || isFull}
          aria-label="Ask Velora"
          hidden={!open}
          style={style}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !e.defaultPrevented) {
              // Menus and dialogs inside handle their own Escape first.
              const inPopup = (e.target as HTMLElement).closest(
                '[role="menu"],[role="dialog"]:not(.vaw)',
              );
              if (inPopup) return;
              e.stopPropagation();
              // Full screen steps back to the floating panel before closing.
              if (isFull) {
                e.preventDefault();
                assistantWidget.setFull(false);
                return;
              }
              close();
            }
          }}
        >
          <header
            className="vaw__head"
            onPointerDown={canMove ? panelDrag.onPointerDown : undefined}
            onDoubleClick={(e) => {
              if (narrow || (e.target as HTMLElement).closest('[data-no-drag]')) return;
              toggleFull();
            }}
            title={canMove ? 'Drag to move. Double-click to fill the window.' : undefined}
          >
            <span className="vaw__mark" aria-hidden>
              <BrandGlyph size={18} />
            </span>
            <span className="vaw__title">Ask Velora</span>
            <span className="vaw__kbd" aria-hidden>
              <Kbd keys={['Ctrl', 'J']} />
            </span>
            <Link
              to="/assistant"
              className="vaw__icon"
              data-no-drag
              aria-label="Open the AI Assistant workspace"
              title="Open the full workspace (scribe, automations, saved questions)"
              onClick={() => assistantWidget.setOpen(false)}
            >
              <ExternalLink size={14} aria-hidden />
            </Link>
            {narrow ? null : (
              <button
                type="button"
                className="vaw__icon"
                data-no-drag
                aria-label={isFull ? 'Exit full screen' : 'Full screen'}
                aria-pressed={isFull}
                title={isFull ? 'Exit full screen (Esc)' : 'Full screen'}
                onClick={toggleFull}
              >
                {isFull ? <Minimize2 size={14} aria-hidden /> : <Maximize2 size={14} aria-hidden />}
              </button>
            )}
            <button
              type="button"
              className="vaw__icon"
              data-no-drag
              aria-label="Close Ask Velora"
              onClick={close}
            >
              <Minus size={15} aria-hidden />
            </button>
          </header>
          {canMove
            ? EDGES.map((edge) =>
                edge === 'nw' ? (
                  <span
                    key={edge}
                    className={`vaw__grip vaw__grip--${edge}`}
                    data-edge={edge}
                    role="separator"
                    tabIndex={0}
                    aria-label="Resize Ask Velora (arrow keys)"
                    aria-orientation="vertical"
                    aria-valuenow={panelSize.width}
                    aria-valuemin={MIN_SIZE.width}
                    title="Drag to resize"
                    onPointerDown={resize.onPointerDown}
                    onKeyDown={resize.onKeyDown}
                  />
                ) : (
                  <span
                    key={edge}
                    className={`vaw__grip vaw__grip--${edge}`}
                    data-edge={edge}
                    aria-hidden
                    onPointerDown={resize.onPointerDown}
                  />
                ),
              )
            : null}
          <div className="vaw__body">
            <Suspense fallback={<div className="vaw__loading">Loading Ask Velora</div>}>
              <WidgetPanel
                pending={pending}
                onPendingConsumed={() => assistantWidget.consume()}
                inputRef={inputRef}
              />
            </Suspense>
          </div>
        </section>
      ) : null}
    </div>
  );
}
